import React from "react";

/**
 * Catches render-time exceptions below it and shows an honest failure screen.
 *
 * Without one of these, a single throw inside any component unmounts the whole
 * React tree and the user is left on a blank white page with no message, no
 * navigation and no way back short of guessing at the URL bar. That is the
 * worst possible failure presentation: it is indistinguishable from the app
 * being gone, and it teaches the user nothing about whether to retry.
 *
 * Two deliberate limits, because a boundary is easy to over-trust:
 *   - It catches errors thrown while RENDERING children. It does NOT catch
 *     errors in event handlers, in async callbacks, or thrown before the first
 *     render (a module-scope throw runs before any boundary exists).
 *   - "Try again" only helps when the cause was transient. It remounts the
 *     subtree with a fresh key; if the same input still throws, the boundary
 *     catches it again and the user sees this screen a second time. That is
 *     correct — a retry that silently swallowed a permanent fault would be a
 *     worse lie than the crash.
 *
 * The detail block is development-only. In a production build the user gets
 * the honest statement and the two actions, not a stack trace.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Console is the only sink this app has. Keep both arguments: the stack on
    // `error` is where it was thrown, `componentStack` is which component tree
    // it was thrown under, and the second is usually the one that identifies it.
    console.error("Unhandled render error:", error, errorInfo?.componentStack);
  }

  handleRetry = () => {
    this.setState((prev) => ({ error: null, resetKey: prev.resetKey + 1 }));
  };

  handleReload = () => {
    window.location.assign("/");
  };

  render() {
    if (!this.state.error) {
      return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    }

    const isDev = process.env.NODE_ENV === "development";

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 px-6 py-12">
        <div className="max-w-lg w-full text-center">
          <p className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-3">
            Case interrupted
          </p>
          <h1 className="text-gray-100 text-2xl font-bold mb-4">Something went wrong on this screen.</h1>
          <p className="text-gray-400 mb-8">
            Your saved cases and progress are stored on the server and are unaffected. Try this
            screen again, or go back to the case selection.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleRetry}
              className="px-5 py-2.5 rounded-md bg-amber-600 hover:bg-amber-500 text-gray-900 font-semibold transition-colors"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-5 py-2.5 rounded-md border border-gray-600 hover:border-gray-400 text-gray-200 font-semibold transition-colors"
            >
              Back to case selection
            </button>
          </div>
          {isDev && (
            <pre className="mt-8 text-left text-xs text-red-300 bg-gray-950 border border-gray-800 rounded-md p-4 overflow-auto max-h-64 whitespace-pre-wrap">
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
